import { useCallback, useEffect, useRef } from 'react';
import { useAppSelector } from '~/redux/hooks';
import useAdminSessionActions from './useAdminSessionActions';

const REFRESH_THRESHOLD_SECONDS = 1800;

// One refresh attempt a minute at most. The server no-ops above the threshold
// anyway, so this is about not making a request per keystroke.
const THROTTLE_MS = 60000;

/**
 * Backoff for a slide that did not land, in milliseconds.
 *
 * A dropped refresh matters more than it looks. Ordinary activity retries it a
 * minute later on its own, but a member who clicks once and then reads for
 * twenty minutes produces no second event — so a single failed request would
 * cost them the session while they sat there working.
 *
 * Bounded rather than persistent: two retries covers a blip, and past that the
 * warning ladder and the expiry check are the honest fallback. Retrying harder
 * would mean hammering a server that is already unwell.
 */
const RETRY_DELAYS_MS = [5000, 15000];

/**
 * Deliberate interaction, not mere presence.
 *
 * `mousemove` is excluded on purpose, and not only for cost. It measures that a
 * pointer moved, which a jiggler, a cat, or a knocked desk does as well as a
 * member does — and with no absolute ceiling on the session any more, a signal
 * that can be produced without a person is a signal that can hold Admin open
 * forever. These four say someone decided to do something.
 */
const ACTIVITY_EVENTS = ['keydown', 'pointerdown', 'scroll', 'touchstart'] as const;

/**
 * Slides the admin session while the member is actually working.
 *
 * Replaces an earlier hook that refreshed on admin route changes. Navigation is
 * a poor proxy for activity: someone filling in a long form or reading a report
 * stays on one path for an hour, makes no requests, and was ejected despite
 * plainly being there. An API-call signal has the same blind spot — a form
 * produces no traffic until it is saved — so the signal has to come from the
 * input layer.
 *
 * ## Cost
 *
 * Input listeners on window are the kind of thing that quietly ruins scrolling,
 * so the cost is bounded four ways:
 *
 * - **Nothing is attached until the session is inside its last 30 minutes.**
 *   `isRefreshDue` is a boolean, not a countdown, so it changes twice per
 *   session — on the way in, and again when a refresh pushes the deadline back
 *   out — rather than on every tick. For the first 90 minutes of a session, and
 *   for every member who never opens Admin at all, this hook registers no
 *   listeners whatsoever.
 * - **`passive: true`.** The browser never has to wait to find out whether
 *   `preventDefault` is coming, so scrolling and touch stay on the compositor.
 * - **The handler cannot re-render.** Everything it reads is a ref and
 *   everything it writes is a ref. A `setState` per scroll event is the version
 *   of this that would be a performance problem.
 * - **It exits on a numeric comparison.** Once the throttle has been paid the
 *   handler is three reads and a return, so a fast typist inside the window
 *   costs a few hundred integer comparisons a second and nothing else.
 *
 * `scroll` is registered in the capture phase because scroll events do not
 * bubble — a scrollable panel inside the page would otherwise never reach
 * window.
 *
 * Unlike the hook it replaces, this deliberately has no "not on mount" rule. A
 * reload must not renew the session, but the member clicking *after* a reload
 * is activity like any other, and the deadline is the server's to move.
 *
 * @param {Number} remainingSeconds seconds of session life left
 * @param {Boolean} [isSuspended] true while the expiry warning is on screen
 * @returns {void}
 */
const useAdminSessionActivity = (remainingSeconds: number, isSuspended = false) => {
    const { onRefresh } = useAdminSessionActions();

    const status = useAppSelector(({ adminMfa }) => adminMfa.status);

    const lastAttemptRef = useRef(0);
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // A boolean rather than the number itself, so crossing into the window
    // re-runs the effect once instead of every time the counter ticks.
    //
    // Zero is excluded: an expired session is the watcher's to resolve, and
    // refreshing into it would race its check with a request the server refuses.
    const isRefreshDue =
        status === 'valid' && remainingSeconds > 0 && remainingSeconds <= REFRESH_THRESHOLD_SECONDS;

    // Suspended once the expiry warning is on screen. The warning cannot be
    // closed -- the member has to choose to stay or to leave -- and a listener
    // that extended the session on any keypress would hand back the dismissal
    // that was deliberately taken away: a click anywhere, the backdrop
    // included, would slide the deadline and the modal would take itself down.
    //
    // Nothing is lost by it. Sliding begins at the refresh threshold, which is
    // wider than the first warning rung, so a member who is actually working is
    // topped up before any warning fires. Reaching one means they had genuinely
    // stopped, and that is the case the modal exists to interrupt.
    const isListening = isRefreshDue && !isSuspended;

    /**
     * Slide, and keep trying briefly if it does not land.
     *
     * Nothing here renders. A refresh the member notices is a refresh that has
     * gone wrong: `onRefresh` writes to the store only when the server actually
     * hands back a session, so a failure leaves the countdown running and the
     * page exactly as it was.
     *
     * @param {Number} attempt how many retries have already been spent
     * @returns {void}
     */
    const onSlide = useCallback(
        (attempt = 0) => {
            onRefresh().then(isDone => {
                if (isDone || attempt >= RETRY_DELAYS_MS.length) return;

                retryTimerRef.current = setTimeout(
                    () => onSlide(attempt + 1),
                    RETRY_DELAYS_MS[attempt]
                );
            });
        },
        [onRefresh]
    );

    const onActivity = useCallback(() => {
        const now = Date.now();

        if (now - lastAttemptRef.current < THROTTLE_MS) return;

        lastAttemptRef.current = now;

        onSlide();
    }, [onSlide]);

    useEffect(() => {
        if (!isListening) return;

        ACTIVITY_EVENTS.forEach(event =>
            window.addEventListener(event, onActivity, { passive: true, capture: true })
        );

        return () => {
            ACTIVITY_EVENTS.forEach(event =>
                window.removeEventListener(event, onActivity, { capture: true })
            );

            // A pending retry outlives the window it was scheduled in, and by
            // the time it fired the session could have been extended, revoked
            // or ended. Whatever comes next owns that decision, not this.
            if (retryTimerRef.current) {
                clearTimeout(retryTimerRef.current);
                retryTimerRef.current = null;
            }
        };
    }, [isListening, onActivity]);
};

export default useAdminSessionActivity;
